import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ClientContextObject, Journey, PayloadObject } from "./index";
import { SendOTPFormProps } from "../features/sendOTPForm/SendOTPForm";
import { OTPInputFormProps } from "../features/OTPInputForm/OTPInputForm";
import { ConfirmationInputFormProps } from "../features/confirmationInputForm/ConfirmationInputForm";
import { InformationResponseProps } from "../features/InformationResponse/InformationResponse";
import { SelectionUIProps } from "../features/selectionUI/SelectionUI";
import { ProcessJsonDataProps } from "../features/ProcessJsonData/ProcessJsonData";

export enum AppState {
  REMEMBER_DEVICE = "REMEMBER_DEVICE",
  USERNAME_PASSWORD = "USERNAME_PASSWORD",
  COLLECT_PASSWORD = "COLLECT_PASSWORD",
  SET_CHALLENGE_QA = "SET_CHALLENGE_QA",
  COLLECT_USERNAME = "COLLECT_USERNAME",
  REGISTRATION_INFORMATION = "REGISTRATION_INFORMATION",
  VALIDATE_CHALLENGE_QA = "VALIDATE_CHALLENGE_QA",
  REGISTRATION_LINK_EXPIRED = "REGISTRATION_LINK_EXPIRED",
  SEND_OTP = "SEND_OTP",
  OTP_INPUT = "OTP_INPUT",
  CONFIRMATION_INPUT = "CONFIRMATION_INPUT",
  INFORMATION_RESPONSE = "INFORMATION_RESPONSE",
  SELECTION_UI = "SELECTION_UI",
  PROCESS_JSON = "PROCESS_JSON",
  CHANGE_PASSWORD = "CHANGE_PASSWORD",
  COLLECT_CURRENT_AND_NEW_PASSWORD = "COLLECT_CURRENT_AND_NEW_PASSWORD"
}

export type FormResolveType = (value: com.ts.mobile.sdk.FormInput | PromiseLike<com.ts.mobile.sdk.FormInput>) => void;

interface StandardFormProps {
  currentResolve: FormResolveType;
  actionContext: com.ts.mobile.sdk.PolicyAction | null;
  clientContext: ClientContextObject | null;
  currentPayload: PayloadObject;
  currentFormId: Journey;
}

export interface OtpInputFormReduxProps{
  resolve: (value: com.ts.mobile.sdk.InputOrControlResponse<com.ts.mobile.sdk.TargetBasedAuthenticatorInput<com.ts.mobile.sdk.OtpInput, com.ts.mobile.sdk.OtpTarget>> | PromiseLike<com.ts.mobile.sdk.InputOrControlResponse<com.ts.mobile.sdk.TargetBasedAuthenticatorInput<com.ts.mobile.sdk.OtpInput, com.ts.mobile.sdk.OtpTarget>>>) => void;
  onResendButtonClick: () => void;
}

export interface AppStateSlice {
  currentState: AppState | undefined;
  redirectionUserId: string;
  currentUIHandlerFunction: string;
  standardFormProps: StandardFormProps | null;
  authenticationFailureMessage: string;
  sendOTPFormProps: SendOTPFormProps | null;
  otpInputFormProps: OtpInputFormReduxProps | null;
  confirmationInputFormProps: ConfirmationInputFormProps | null;
  informationResponseProps: InformationResponseProps | null;
  selectionUIProps: SelectionUIProps | null;
  processJsonDataProps: ProcessJsonDataProps | null;
  currentErrorMessage: string;
}

export const initialState: AppStateSlice = {
  currentState: undefined,
  currentUIHandlerFunction: "",
  redirectionUserId: "",
  standardFormProps: null,
  authenticationFailureMessage: "",
  sendOTPFormProps: null,
  otpInputFormProps: null,
  confirmationInputFormProps: null,
  informationResponseProps: null,
  selectionUIProps: null,
  processJsonDataProps: null,
  currentErrorMessage: ""
}

const appStateSlice = createSlice({
  name: "appState",
  initialState,
  reducers: {
    updateCurrentStateWithStandardFormProps(state: AppStateSlice, action: PayloadAction<{ state: AppState, resolve: FormResolveType, actionContext: com.ts.mobile.sdk.PolicyAction | null, payload: PayloadObject, errorMessage: string, clientContext: ClientContextObject | null, formId: Journey }>) {
      state.currentState = action.payload.state;
      state.currentErrorMessage= action.payload.errorMessage;
      state.standardFormProps = {
        currentResolve: action.payload.resolve,
        actionContext: action.payload.actionContext,
        clientContext: action.payload.clientContext,
        currentPayload: action.payload.payload,
        currentFormId: action.payload.formId,
      }
    },
    updateStandardFormErrorMessage(state: AppStateSlice, action: PayloadAction<string>) {
      state.currentErrorMessage = action.payload;
    },
    updateCurrentUIHandlerFunction(state: AppStateSlice, action: PayloadAction<string>) {
      state.currentUIHandlerFunction = action.payload;
    },
    updateAuthenticationFailureMessage(state: AppStateSlice, action: PayloadAction<string>) {
      state.authenticationFailureMessage = action.payload;
    },
    updateCurrentStateWithSendOTPFormProps(state: AppStateSlice, action: PayloadAction<{ state: AppState, props: SendOTPFormProps }>) {
      state.currentState = action.payload.state;
      state.sendOTPFormProps = action.payload.props;
    },
    updateCurrentStateWithOTPInputFormProps(state: AppStateSlice, action: PayloadAction<{ state: AppState, props: OTPInputFormProps }>) {
      state.currentState = action.payload.state;
      state.otpInputFormProps = action.payload.props;
    },
    updateCurrentStateWithConfirmationInputFormProps(state: AppStateSlice, action: PayloadAction<{ state: AppState, props: ConfirmationInputFormProps }>) {
      state.currentState = action.payload.state;
      state.confirmationInputFormProps = action.payload.props;
    },
    updateCurrentStateWithInformationResponseProps(state: AppStateSlice, action: PayloadAction<{ state: AppState, props: InformationResponseProps }>) {
      state.currentState = action.payload.state;
      state.informationResponseProps = action.payload.props;
    },
    updateCurrentStateWithSelectionUIProps(state: AppStateSlice, action: PayloadAction<{ state: AppState, props: SelectionUIProps }>) {
      state.currentState = action.payload.state;
      state.selectionUIProps = action.payload.props;
    },
    updateCurrentStateWithProcessJsonDataProps(state: AppStateSlice, action: PayloadAction<{ state: AppState, props: ProcessJsonDataProps }>) {
      state.currentState = action.payload.state;
      state.processJsonDataProps = action.payload.props;
    },
    updateRedirectionUserId(state: AppStateSlice, action: PayloadAction<string>) {
      state.redirectionUserId = action.payload;
    },
    clearFormState(state: AppStateSlice) {
      //state.currentState = undefined; //dont reset so we can see previous state
      state.redirectionUserId = "";
      state.standardFormProps = null;
      state.sendOTPFormProps = null;
      state.otpInputFormProps = null;
      state.confirmationInputFormProps = null;
      state.informationResponseProps = null;
      state.selectionUIProps = null;
      state.processJsonDataProps = null;
    },
    revertToUserNameForm(state: AppStateSlice) {
      state.currentState = AppState.USERNAME_PASSWORD;
      state.redirectionUserId = "";
      state.standardFormProps = null;
      state.sendOTPFormProps = null;
      state.otpInputFormProps = null;
      state.confirmationInputFormProps = null;
      state.informationResponseProps = null;
      state.selectionUIProps = null;
      state.processJsonDataProps = null;
    },
    revertToChangePasswordForm(state: AppStateSlice) {
      state.currentState = AppState.CHANGE_PASSWORD;
      state.redirectionUserId = "";
      state.standardFormProps = null;
      state.sendOTPFormProps = null;
      state.otpInputFormProps = null;
      state.confirmationInputFormProps = null;
      state.informationResponseProps = null;
      state.selectionUIProps = null;
      state.processJsonDataProps = null;
    },
    revertToForgetPasswordForm(state: AppStateSlice) {
      state.currentState = AppState.COLLECT_USERNAME;
      state.redirectionUserId = "";
      state.standardFormProps = null;
      state.sendOTPFormProps = null;
      state.otpInputFormProps = null;
      state.confirmationInputFormProps = null;
      state.informationResponseProps = null;
      state.selectionUIProps = null;
      state.processJsonDataProps = null;
    },
    updateCurrentState(state: AppStateSlice, action: PayloadAction<{state: AppState}>) {
      state.currentState = action.payload.state; }
  }
 
});

export const {
  updateCurrentStateWithStandardFormProps,
  updateStandardFormErrorMessage,
  updateCurrentUIHandlerFunction,
  updateAuthenticationFailureMessage,
  updateCurrentStateWithSendOTPFormProps,
  updateCurrentStateWithOTPInputFormProps,
  updateCurrentStateWithConfirmationInputFormProps,
  updateCurrentStateWithInformationResponseProps,
  updateCurrentStateWithSelectionUIProps,
  updateCurrentStateWithProcessJsonDataProps,
  updateRedirectionUserId,
  clearFormState,
  revertToUserNameForm,
  revertToChangePasswordForm,
  revertToForgetPasswordForm,
  updateCurrentState,
} = appStateSlice.actions;

export default appStateSlice.reducer;